import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { oneKlicks } from '../SDK/oneclick';
import { toast } from 'react-toastify';
import { Badge, Button as BSButton, Card } from 'react-bootstrap';
import { ReloadOutlined, BellOutlined, CheckOutlined, DeleteOutlined, InfoCircleOutlined, MessageOutlined, CalendarOutlined, SafetyCertificateOutlined } from '@ant-design/icons';

const UserNotifications = () => {
    const { userData } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [notificationsLoading, setNotificationsLoading] = useState(true);
    const [notifications, setNotifications] = useState([]);
    const [notificationSettings, setNotificationSettings] = useState({
        appointment_reminders: true,
        sms_notifications: true,
        email_notifications: true
    });
    const [activeTab, setActiveTab] = useState('all');

    useEffect(() => {
        const init = async () => {
            await fetchUserProfile();
            await fetchNotifications();
        };
        init();
    }, [userData?.id]);

    const fetchUserProfile = async () => {
        if (!userData?.id) return;
        try {
            setLoading(true);
            const response = await oneKlicks.read('', 'users', { id: userData?.id }, '', '', '', '');
            if (response.success && response.data.length > 0) {
                const user = response.data[0];
                if (user.notification_settings) {
                    try {
                        const settings = JSON.parse(user.notification_settings);
                        setNotificationSettings({ ...notificationSettings, ...settings });
                    } catch (e) {
                        console.log('Using default notification settings');
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching user profile:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchNotifications = async () => {
        if (!userData?.id) return;
        try {
            setNotificationsLoading(true);
            // Try fetching from notifications table
            let response = await oneKlicks.read('', 'notifications', { user_id: userData?.id }, 'id DESC', '', '', '');

            if (!response.success || (response.data && response.data.length === 0)) {
                // Fallback to activities if notifications is empty or fails
                response = await oneKlicks.read('', 'activities', { user_id: userData?.id }, 'id DESC', '', '', '');
            }

            if (response.success) {
                setNotifications(response.data || []);
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setNotificationsLoading(false);
        }
    };

    const handleNotificationSettingsUpdate = async (settings) => {
        try {
            const data = {
                notification_settings: JSON.stringify(settings || notificationSettings),
                updated_at: new Date().toISOString().slice(0, 19).replace('T', ' ')
            };
            const response = await oneKlicks.update('', 'users', data, { id: userData?.id });
            if (response.success) {
                toast.success('Settings updated successfully');
            }
        } catch (error) {
            toast.error('Failed to update settings');
        }
    };

    const handleToggleChange = async (settingName, value) => {
        const newSettings = { ...notificationSettings, [settingName]: value };
        setNotificationSettings(newSettings);
        await handleNotificationSettingsUpdate(newSettings);
    };

    const markAsRead = async (id) => {
        try {
            const response = await oneKlicks.update('', 'notifications', { is_read: 1 }, { id });
            if (response.success) {
                setNotifications(notifications.map(n => n.id === id ? { ...n, is_read: 1 } : n));
            }
        } catch (error) {
            console.error(error);
        }
    };

    const deleteNotification = async (id) => {
        try {
            const response = await oneKlicks.delete('', 'notifications', { id });
            if (response.success) {
                setNotifications(notifications.filter(n => n.id !== id));
                toast.success('Notification deleted');
            }
        } catch (error) {
            toast.error('Delete failed');
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' }) + ' ' +
            date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    };

    const getNotificationIcon = (item) => {
        const type = (item.type || item.notification_type || '').toLowerCase();
        if (type.includes('app') || type.includes('doc')) return <CalendarOutlined style={{ color: '#0ea5e9' }} />;
        if (type.includes('msg') || type.includes('chat')) return <MessageOutlined style={{ color: '#10b981' }} />;
        if (type.includes('sec') || type.includes('pass')) return <SafetyCertificateOutlined style={{ color: '#f43f5e' }} />;
        return <BellOutlined style={{ color: '#8b5cf6' }} />;
    };

    const getIconBg = (item) => {
        const type = (item.type || item.notification_type || '').toLowerCase();
        if (type.includes('app') || type.includes('doc')) return 'rgba(14, 165, 233, 0.1)';
        if (type.includes('msg') || type.includes('chat')) return 'rgba(16, 185, 129, 0.1)';
        if (type.includes('sec') || type.includes('pass')) return 'rgba(244, 63, 94, 0.1)';
        return 'rgba(139, 92, 246, 0.1)';
    };

    if (loading && notifications.length === 0) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status"></div>
                <p className="mt-2 text-muted">Loading your notifications...</p>
            </div>
        );
    }

    return (
        <section className="dc-haslayout dc-dbsectionspace">
            <style>{`
                .notif-card {
                    border: none;
                    border-radius: 16px;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.05);
                    background: #fff;
                    overflow: hidden;
                }
                .notif-item {
                    padding: 20px 24px;
                    border-bottom: 1px solid #f1f5f9;
                    transition: all 0.2s ease;
                    display: flex;
                    gap: 16px;
                    align-items: flex-start;
                }
                .notif-item:hover {
                    background-color: #f8fafc;
                }
                .notif-item.unread {
                    background-color: #f0f9ff;
                    border-left: 4px solid #0ea5e9;
                }
                .notif-icon-box {
                    width: 44px;
                    height: 44px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 18px;
                    flex-shrink: 0;
                }
                .notif-content {
                    flex: 1;
                }
                .notif-title {
                    font-weight: 700;
                    color: #1e293b;
                    font-size: 15px;
                    margin-bottom: 4px;
                }
                .notif-text {
                    color: #64748b;
                    font-size: 14px;
                    line-height: 1.5;
                    margin-bottom: 8px;
                }
                .notif-time {
                    font-size: 12px;
                    color: #94a3b8;
                }
                .notif-actions {
                    display: flex;
                    gap: 8px;
                }
                .action-circle {
                    width: 32px;
                    height: 32px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border: 1px solid #e2e8f0;
                    background: #fff;
                    color: #94a3b8;
                    cursor: pointer;
                    transition: all 0.2s;
                }
                .action-circle:hover {
                    background: #f1f5f9;
                    color: var(--themecolor);
                    border-color: var(--themecolor);
                }
                .action-circle.delete:hover {
                    background: #fef2f2;
                    color: #ef4444;
                    border-color: #ef4444;
                }
                .settings-tray {
                    background: #fff;
                    border-radius: 16px;
                    padding: 24px;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.05);
                }
                .setting-row {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 16px 0;
                    border-bottom: 1px solid #f1f5f9;
                }
                .setting-row:last-child {
                    border-bottom: none;
                }
                .setting-info {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }
                .setting-icon {
                    width: 36px;
                    height: 36px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: #f8fafc;
                    color: #64748b;
                }
            `}</style>

            <div className="subbody" style={{ width: '100%', padding: '2% 4%' }}>
                <div className="container-fluid">
                    <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
                        <div>
                            <h2 style={{ fontSize: '26px', fontWeight: '800', color: '#0f172a', margin: 0 }}>Notifications</h2>
                            <p className="text-muted mb-0">Manage your notifications and preferences</p>
                        </div>
                        <BSButton
                            variant="primary"
                            onClick={fetchNotifications}
                            style={{
                                borderRadius: '12px',
                                background: 'var(--themecolor)',
                                border: 'none',
                                padding: '10px 24px',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            <ReloadOutlined /> Refresh
                        </BSButton>
                    </div>

                    <div className="row g-4">
                        <div className="col-xl-8">
                            <Card className="notif-card">
                                <div className="p-4 border-bottom d-flex align-items-center justify-content-between">
                                    <h5 className="mb-0 font-weight-bold">Recent Alerts</h5>
                                    <div className="d-flex gap-2">
                                        <button
                                            className={`btn btn-sm ${activeTab === 'all' ? 'btn-light' : 'btn-white'}`}
                                            onClick={() => setActiveTab('all')}
                                            style={{ borderRadius: '8px', fontWeight: '600' }}
                                        >
                                            All
                                        </button>
                                        <button
                                            className={`btn btn-sm ${activeTab === 'unread' ? 'btn-light' : 'btn-white'}`}
                                            onClick={() => setActiveTab('unread')}
                                            style={{ borderRadius: '8px', fontWeight: '600' }}
                                        >
                                            Unread
                                        </button>
                                    </div>
                                </div>

                                {notificationsLoading ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary spinner-border-sm" role="status"></div>
                                        <p className="mt-2 text-muted small">Checking for updates...</p>
                                    </div>
                                ) : notifications.length > 0 ? (
                                    <div className="notif-list">
                                        {notifications
                                            .filter(n => activeTab === 'all' || !n.is_read)
                                            .map((notif) => (
                                                <div key={notif.id} className={`notif-item ${!notif.is_read ? 'unread' : ''}`}>
                                                    <div className="notif-icon-box" style={{ background: getIconBg(notif) }}>
                                                        {getNotificationIcon(notif)}
                                                    </div>
                                                    <div className="notif-content">
                                                        <div className="d-flex justify-content-between">
                                                            <div className="notif-title">{notif.title || notif.text || 'Account Update'}</div>
                                                            <div className="notif-actions">
                                                                {!notif.is_read && (
                                                                    <div className="action-circle" onClick={() => markAsRead(notif.id)} title="Mark as read">
                                                                        <CheckOutlined fontSize="12" />
                                                                    </div>
                                                                )}
                                                                <div className="action-circle delete" onClick={() => deleteNotification(notif.id)} title="Delete">
                                                                    <DeleteOutlined fontSize="12" />
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="notif-text">{notif.message || notif.description || notif.text}</div>
                                                        <div className="notif-time">
                                                            <i className="ti-calendar me-1"></i> {formatDate(notif.created_at)}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-5">
                                        <div style={{ opacity: 0.1, fontSize: '60px', marginBottom: '10px' }}>
                                            <BellOutlined />
                                        </div>
                                        <h6 className="text-muted">No notifications yet</h6>
                                        <p className="small text-muted mb-0">We'll notify you when something important happens.</p>
                                    </div>
                                )}
                            </Card>
                        </div>

                        <div className="col-xl-4">
                            <div className="settings-tray">
                                <h5 className="mb-4 font-weight-bold" style={{ fontSize: '18px' }}>Preferences</h5>

                                <div className="setting-row">
                                    <div className="setting-info">
                                        <div className="setting-icon"><BellOutlined /></div>
                                        <div>
                                            <div style={{ fontWeight: '600', fontSize: '14px' }}>Appointments</div>
                                            <div style={{ fontSize: '11px', color: '#94a3b8' }}>Status & Reminder alerts</div>
                                        </div>
                                    </div>
                                    <div className="dc-on-off">
                                        <input
                                            type="checkbox"
                                            checked={notificationSettings.appointment_reminders}
                                            onChange={(e) => handleToggleChange('appointment_reminders', e.target.checked)}
                                            id="set_app"
                                        />
                                        <label htmlFor="set_app"><i></i></label>
                                    </div>
                                </div>

                                <div className="setting-row">
                                    <div className="setting-info">
                                        <div className="setting-icon"><i className="ti-mobile"></i></div>
                                        <div>
                                            <div style={{ fontWeight: '600', fontSize: '14px' }}>SMS Alerts</div>
                                            <div style={{ fontSize: '11px', color: '#94a3b8' }}>Critical real-time texts</div>
                                        </div>
                                    </div>
                                    <div className="dc-on-off">
                                        <input
                                            type="checkbox"
                                            checked={notificationSettings.sms_notifications}
                                            onChange={(e) => handleToggleChange('sms_notifications', e.target.checked)}
                                            id="set_sms_tray"
                                        />
                                        <label htmlFor="set_sms_tray"><i></i></label>
                                    </div>
                                </div>

                                <div className="setting-row">
                                    <div className="setting-info">
                                        <div className="setting-icon"><i className="ti-email"></i></div>
                                        <div>
                                            <div style={{ fontWeight: '600', fontSize: '14px' }}>Email Summary</div>
                                            <div style={{ fontSize: '11px', color: '#94a3b8' }}>Weekly health digests</div>
                                        </div>
                                    </div>
                                    <div className="dc-on-off">
                                        <input
                                            type="checkbox"
                                            checked={notificationSettings.email_notifications}
                                            onChange={(e) => handleToggleChange('email_notifications', e.target.checked)}
                                            id="set_email_tray"
                                        />
                                        <label htmlFor="set_email_tray"><i></i></label>
                                    </div>
                                </div>

                                <div className="mt-5 text-center p-3" style={{ background: '#f8fafc', borderRadius: '12px' }}>
                                    <InfoCircleOutlined style={{ color: '#0ea5e9', fontSize: '24px', marginBottom: '10px' }} />
                                    <p style={{ fontSize: '11px', color: '#64748b', margin: 0 }}>
                                        Updating these settings will change how we communicate with you.
                                        Critical account alerts cannot be disabled.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default UserNotifications;
